import { Link } from "@bond-london/gatsby-plugin-cms-i18next";
import { IGatsbySvg } from "@bond-london/gatsby-transformer-extracted-svg";
import { IRichTextInformation } from "@bond-london/graphcms-rich-text";
import classNames from "classnames";
import React from "react";
import { RTF } from "../elements/RTF";
import { useEntryAnimation } from "../hooks/Visibility";
import { lookupColourString } from "../lookups";
import {
  HalfWidthClassName,
  HeadingRTFOverrides,
  MainWidthClassName,
  P1RTFOverrides,
} from "../styles";

export const ServicesSlide: React.FC<{
  id: string;
  title?: string | null;
  headline?: string | null;
  message?: IRichTextInformation | null;
  shape?: IGatsbySvg | null;
  link?: string | null;
  linkTitle?: string | null;
  showLinkAsButton?: boolean | null;
  background: string | null;
}> = ({ id, title, headline, message, shape, link, linkTitle, background }) => {
  const [sectionRef, animationMode] = useEntryAnimation();
  const backgroundColour = background ?? "Lazer";
  return (
    <section
      ref={sectionRef}
      data-component="Services Slide"
      id={id}
      className={classNames(
        "relative mb-40px h-full",
        lookupColourString(backgroundColour, "bg"),
        lookupColourString(backgroundColour, "textForBg"),
      )}
    >
      <div
        className={classNames(
          "page-grid max-width relative h-full auto-rows-max",
        )}
      >
        {shape && (
          <div
            className={classNames(
              animationMode,
              "animate-appear",
              "absolute flex items-center justify-end",
              "bottom-mobile-margin left-mobile-margin right-mobile-margin top-mobile-margin",
              "md:bottom-tablet-margin md:left-tablet-margin md:right-tablet-margin md:top-tablet-margin",
              "lg:bottom-desktop-margin lg:left-desktop-margin lg:right-desktop-margin lg:top-desktop-margin",
            )}
          >
            <img
              src={shape.encoded ?? shape.encodedUrl}
              className="h-full object-contain"
              alt=""
            />
          </div>
        )}
        {headline && (
          <h3
            className={classNames(
              "h6 relative mt-48px text-hyacinth",
              HalfWidthClassName,
              "row-start-2 lg:row-start-1",
              animationMode,
              "animate-enter-from-bottom",
            )}
          >
            {headline}
          </h3>
        )}
        {title && (
          <h1
            className={classNames(
              "h1 relative mt-20px",
              HalfWidthClassName,
              "row-start-3 lg:row-start-2",
              animationMode,
              "animate-enter-from-bottom",
            )}
          >
            {title.toUpperCase()}
          </h1>
        )}
        {message && (
          <RTF
            className={classNames(
              "relative mt-30px",
              MainWidthClassName,
              "row-start-4 lg:row-start-3",
              animationMode,
              "animate-enter-from-bottom",
            )}
            content={message}
            classNameOverrides={{ ...P1RTFOverrides, ...HeadingRTFOverrides }}
          />
        )}
        {link && (
          <div
            className={classNames(
              "col-span-2 lg:col-span-4 lg:col-start-3",
              "flex justify-start",
              animationMode,
              "animate-enter-from-bottom",
            )}
          >
            <Link className="servicesButton lg:mt-5" to={link}>
              {linkTitle ?? "Read more"}
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};
